import { ApplianceId, Scenario, ScenarioId } from '../../engine/types';

import { byHourSegments, CLEAR_SKY } from './curves';

export const lovelySpringDay: Scenario = {
  description: 'Mild weather, clear skies. A gentle first outage — if you keep the family from frittering it away.',
  events: [
    {
      atMinute: 0,
      from: 'Sam',
      id: 'spring-morning',
      responses: [{ effect: { moodDelta: 2 }, label: 'Have a good day!' }],
      text: 'Off to work! Gorgeous out today. Crew’s coming Saturday for the gutters — have a good one!',
    },
    {
      atMinute: 60,
      from: 'Sam',
      id: 'spring-warning',
      responses: [
        { effect: { moodDelta: 2 }, label: 'We’ve got this.' },
        {
          effect: { moodDelta: 0, reaction: 'Sam: lol you’ll be fine. Probably.' },
          label: 'Wait, WHAT?',
        },
      ],
      text: 'Heads up — utility says emergency line work starts at noon. Power could be out until tomorrow.',
    },
    {
      atMinute: 120,
      from: 'Grid Alert',
      id: 'spring-outage',
      responses: [],
      text: 'The power is out. Your house is on its own — keep everyone comfortable until the grid returns at noon tomorrow.',
    },
    {
      atMinute: 210,
      from: 'Sam',
      id: 'spring-laundry',
      responses: [
        {
          effect: { durationMinutes: 90, moodDelta: 0, startLoads: [ApplianceId.LAUNDRY] },
          grantsAsk: true,
          label: 'Running it now',
        },
        {
          effect: { moodDelta: 1, reaction: 'Sam: oh right!! save the juice' },
          label: 'It can wait — power’s out, remember?',
        },
      ],
      text: 'Did you ever run that load of laundry? I need my lucky shirt tomorrow.',
    },
    {
      atMinute: 330,
      from: 'Home',
      id: 'spring-kids-home',
      responses: [],
      text: 'Riley and Jojo are home from school.',
    },
    {
      atMinute: 345,
      from: 'Jojo',
      id: 'spring-snack',
      requiresKidsHome: true,
      responses: [
        {
          effect: { durationMinutes: 30, moodDelta: 4, startLoads: [ApplianceId.OVEN] },
          grantsAsk: true,
          label: 'Go for it',
        },
        {
          effect: { moodDelta: -1, reaction: 'Jojo: cold cheese is fine i guess' },
          label: 'How about a no-grill sandwich',
        },
      ],
      text: 'can i make grilled cheese',
    },
    {
      atMinute: 390,
      from: 'Riley',
      id: 'spring-video-games',
      requiresKidsHome: true,
      responses: [
        {
          effect: { durationMinutes: 60, moodDelta: 5, startLoads: [ApplianceId.TV_CONSOLE] },
          grantsAsk: true,
          label: 'Sure, one hour',
        },
        {
          effect: { moodDelta: 2, reaction: 'Riley: FINE but rematch later' },
          label: 'It’s gorgeous out — backyard time',
        },
      ],
      text: 'homework’s done. mario kart??',
    },
    {
      atMinute: 450,
      from: 'Sam',
      id: 'spring-dinner',
      responses: [
        {
          effect: { durationMinutes: 60, moodDelta: 5, startLoads: [ApplianceId.OVEN] },
          grantsAsk: true,
          label: 'Pasta night',
        },
        {
          effect: { moodDelta: 1, reaction: 'Sam: smart. romantic, even. candlelit sandwiches.' },
          label: 'Keep it simple — sandwiches and salad',
        },
      ],
      text: 'Home! Want me to do the big pasta night or keep it simple?',
    },
    {
      atMinute: 510,
      from: 'Jojo',
      id: 'spring-movie',
      requiresKidsHome: true,
      responses: [
        {
          effect: { durationMinutes: 120, moodDelta: 5, startLoads: [ApplianceId.TV_CONSOLE] },
          grantsAsk: true,
          label: 'Movie night',
        },
        {
          effect: { moodDelta: 3, reaction: 'Jojo: i get to be the dog' },
          label: 'Board games by lantern light',
        },
      ],
      text: 'MOVIE NIGHT?? 🍿🍿🍿',
    },
    {
      atMinute: 630,
      from: 'Sam',
      id: 'spring-ev',
      requiresLoadOff: ApplianceId.EV_CHARGER,
      responses: [
        {
          effect: { durationMinutes: 180, moodDelta: 0, startLoads: [ApplianceId.EV_CHARGER] },
          grantsAsk: true,
          label: 'Plug it in',
        },
        {
          effect: { moodDelta: 1, reaction: 'Sam: good call. it can sip tomorrow.' },
          label: 'Skip it — save the battery',
        },
      ],
      text: 'Should I plug in the car? It’s at half. I’m home tomorrow anyway.',
    },
    {
      atMinute: 720,
      from: 'Home',
      id: 'spring-lights-out',
      responses: [],
      text: 'Lights out. The house settles in for the night.',
    },
    {
      atMinute: 1260,
      from: 'Riley',
      id: 'spring-breakfast',
      requiresKidsHome: true,
      responses: [
        {
          effect: { durationMinutes: 30, moodDelta: 4, startLoads: [ApplianceId.OVEN] },
          grantsAsk: true,
          label: 'Pancakes it is',
        },
        { effect: { moodDelta: -1, reaction: 'Riley: cereal. again. incredible.' }, label: 'Cereal today, chef' },
      ],
      text: 'pancakes???',
    },
    {
      atMinute: 1380,
      from: 'Sam',
      id: 'spring-shower',
      responses: [
        {
          effect: { durationMinutes: 30, moodDelta: 3, startLoads: [ApplianceId.WATER_HEATER] },
          grantsAsk: true,
          label: 'Go for it',
        },
        {
          effect: {
            durationMinutes: 10,
            moodDelta: 1,
            reaction: 'Sam: in and out. like a heist.',
            startLoads: [ApplianceId.WATER_HEATER],
          },
          label: 'Make it a quick one',
        },
      ],
      text: 'Do I have time for a hot shower before my noon meeting?',
    },
  ],
  id: ScenarioId.LOVELY_SPRING_DAY,
  kidsHomeFromMinute: 330,
  name: 'Lovely Spring Day',
  outdoorTempByHour: byHourSegments([
    [6, 48],
    [9, 52],
    [12, 62],
    [15, 70],
    [18, 68],
    [21, 58],
    [24, 50],
  ]),
  solarFactorByHour: CLEAR_SKY,
  solarFactorByHourDayTwo: CLEAR_SKY,
};
